import React, { useEffect, useState, cloneElement, Component } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ChevronDown, Calendar, MessageCircle, ArrowLeft, Phone, Clock } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { SERVICES_DATA } from '../data/servicesData';
import { twMerge } from 'tailwind-merge';
export function ServiceDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const service = SERVICES_DATA.find(s => s.slug === slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!service) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-id="element-801" data-mp="13u1u9y1vcka7">
        <h1 className="text-3xl font-heading font-bold text-brand-navy mb-4" data-id="element-802" data-mp="3sh6t3rrpd3m8">
          Service Not Found
        </h1>
        <p className="text-slate-600 mb-8" data-id="element-803" data-mp="18h9gh6kol4bn">
          The service you are looking for does not exist or has been moved.
        </p>
        <Button href="/services" variant="primary" data-id="element-804" data-mp="1df1iitf1i6tk">
          View All Services
        </Button>
      </div>;
  }
  const whatsappMessage = encodeURIComponent(`Hello Maisha Medical Center.\nI would like to inquire about ${service.title}.\nPlease assist me.`);
  return <div className="min-h-screen bg-brand-light pb-20" data-id="element-805" data-mp="pq050rrco5hf">
      {/* Hero Band */}
      <div className="bg-brand-navy py-16 relative overflow-hidden" data-id="element-806" data-mp="uh8gdtc8q0x8">
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-807" data-mp="uijmhzt5rkri">
          <button onClick={() => navigate('/services')} className="flex items-center gap-2 text-brand-gold hover:text-white transition-colors text-sm font-medium mb-8" data-id="element-808" data-mp="ftx5zursomyp">
            <ArrowLeft className="w-4 h-4" data-id="element-809" data-mp="6s60or162i5d" /> Back to Services
          </button>
          <div className="flex items-center gap-4 mb-6" data-id="element-810" data-mp="bpph9xlc770h">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-brand-gold backdrop-blur-sm" data-id="element-811" data-mp="2dfwlyo54sngc">
              {cloneElement(service.icon as React.ReactElement, {
              className: 'w-8 h-8'
            })}
            </div>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white" data-id="element-812" data-mp="23qd51o0zb3o4">
              {service.title}
            </h1>
          </div>
          <p className="text-lg text-slate-300 max-w-3xl leading-relaxed" data-id="element-813" data-mp="2wfai9cqc2aa0">
            {service.shortDesc}
          </p>
        </div>
      </div>

      <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8 mt-12" data-id="element-814" data-mp="2rgh4dj74wh9b">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-815" data-mp="2nd1bmxy6r11w">
          {/* Main Content */}
          <div className="w-full lg:w-2/3 space-y-12" data-id="element-816" data-mp="2ifsp6l608auu">
            {/* Overview */}
            <section data-id="element-817" data-mp="1bzgp2ejmgiwm">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-4" data-id="element-818" data-mp="1bzyr5rsytrcg">
                Overview
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed" data-id="element-819" data-mp="16yj8u7i69yxr">
                {service.overview}
              </p>
            </section>

            {/* Sub-services */}
            {service.subServices && service.subServices.length > 0 && <section data-id="element-820" data-mp="3rnckgtk600pg">
                <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-821" data-mp="3rn2rcx3tpdfj">
                  Our {service.title} Services
                </h2>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 border-t border-slate-200" data-id="element-822" data-mp="589tzws80rcv">
                  {service.subServices.map(item => <li key={item} className="flex items-center gap-4 py-4 border-b border-slate-200" data-id="element-823" data-mp="a5ta728hbe7g">
                      <span className="w-10 h-10 rounded-xl bg-white border border-slate-100 flex items-center justify-center text-brand-gold shrink-0" data-id="element-824" data-mp="j8cc9k4fxmsn">
                        {cloneElement(service.icon as React.ReactElement, {
                    className: 'w-5 h-5'
                  })}
                      </span>
                      <span className="font-heading font-semibold text-brand-navy" data-id="element-825" data-mp="1pnfu8dyvo0xi">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </section>}

            {/* Benefits */}
            <section className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100" data-id="element-826" data-mp="1updlnvhfm21j">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-827" data-mp="16zt1585mqwq5">
                Key Benefits
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-828" data-mp="121hokpnya848">
                {service.benefits.map((benefit, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-829" data-mp="1bxujtrz58u13" data-mpi={idx}>
                    <CheckCircle2 className="w-6 h-6 text-brand-gold shrink-0 mt-0.5" data-id="element-830" data-mp="1bwjc5h2kv24p" />
                    <span className="text-slate-700 font-medium" data-id="element-831" data-mp="o6p3zct7e1qs">
                      {benefit}
                    </span>
                  </div>)}
              </div>
            </section>

            {/* Process */}
            <section data-id="element-832" data-mp="2zn8rnr55z3ao">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-8" data-id="element-833" data-mp="2znsdvk1ukdui">
                What to Expect
              </h2>
              <div className="space-y-6 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent" data-id="element-834" data-mp="2lxf3lb1hgn20">
                {service.process.map((step, idx) => <div key={idx} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-835" data-mp="3ijqsqz5wkfjl" data-mpi={idx}>
                    <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-brand-light bg-brand-navy text-white font-bold shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-sm z-10" data-id="element-836" data-mp="1t76kngz4hymd">
                      {idx + 1}
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-white p-6 rounded-2xl shadow-sm border border-slate-100" data-id="element-837" data-mp="3uwm861q2hjvh">
                      <h3 className="font-heading font-bold text-brand-navy text-lg mb-2" data-id="element-838" data-mp="3q4b4cns7hdxn">
                        {step.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed" data-id="element-839" data-mp="3h4o4i6f4kg6p">
                        {step.desc}
                      </p>
                    </div>
                  </div>)}
              </div>
            </section>

            {/* FAQs */}
            <section data-id="element-840" data-mp="378uizxjfhccs">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-841" data-mp="3356czjag2otn">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4" data-id="element-842" data-mp="15e3pr45msvsa">
                {service.faqs.map((faq, idx) => {
                const isOpen = openFaqIndex === idx;
                return <div key={idx} className={twMerge('bg-white border rounded-2xl overflow-hidden transition-all duration-300', isOpen ? 'border-brand-navy shadow-soft' : 'border-slate-200 hover:border-brand-navy/30')} data-id="element-843" data-mp="2jc7xic0f96ns" data-mpi={idx}>
                      <button onClick={() => setOpenFaqIndex(isOpen ? null : idx)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-844" data-mp="20dlugrlch5h8">
                        <span className="font-heading font-semibold text-lg text-brand-navy pr-8" data-id="element-845" data-mp="29flelsrnopoe">
                          {faq.q}
                        </span>
                        <ChevronDown className={twMerge('w-5 h-5 text-brand-red shrink-0 transition-transform duration-300', isOpen ? 'rotate-180' : '')} data-id="element-846" data-mp="3krvytasc3dh8" />
                      </button>
                      <div className={twMerge('px-6 overflow-hidden transition-all duration-300 ease-in-out', isOpen ? 'max-h-96 pb-5 opacity-100' : 'max-h-0 opacity-0')} data-id="element-847" data-mp="2wdr1wu1e5fek">
                        <p className="text-slate-600 leading-relaxed" data-id="element-848" data-mp="3f5a26t416fcw">
                          {faq.a}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-1/3 space-y-6" data-id="element-849" data-mp="1yt4px47ucvxf">
            {/* Action Card */}
            <div className="bg-white p-8 rounded-2xl shadow-soft border border-slate-100 sticky top-28" data-id="element-850" data-mp="1ur9siat172ox">
              <h3 className="font-heading font-bold text-xl text-brand-navy mb-2" data-id="element-851" data-mp="1kmbak467kd5a">
                Ready to get started?
              </h3>
              <p className="text-slate-600 text-sm mb-8" data-id="element-852" data-mp="1oq97q9bz3dvp">
                Book your {service.title.toLowerCase()} appointment today.
              </p>

              <div className="space-y-4" data-id="element-853" data-mp="1fv1lb4oknbtx">
                <Button href={`/book?service=${service.slug}`} variant="primary" fullWidth icon={<Calendar className="w-5 h-5" data-id="element-855" data-mp="15xx7pt8skooq" />} data-id="element-854" data-mp="1fu0b12fdqtd9">
                  Book Appointment
                </Button>
                <Button href={`https://wa.me/26773241613?text=${whatsappMessage}`} target="_blank" variant="outline" fullWidth icon={<MessageCircle className="w-5 h-5 text-[#25D366]" data-id="element-857" data-mp="2mcgaresmoy5y" />} className="border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-brand-navy" data-id="element-856" data-mp="rz7743mrk6vm">
                  Inquire via WhatsApp
                </Button>
              </div>

              <hr className="my-8 border-slate-100" data-id="element-858" data-mp="vt6qglwvoxfe" />

              <div className="space-y-4" data-id="element-859" data-mp="vtzrr7d2iuq5">
                <div className="flex items-start gap-3" data-id="element-860" data-mp="i1rvdgpp55rz">
                  <Phone className="w-5 h-5 text-brand-gold shrink-0 mt-0.5" data-id="element-861" data-mp="m2brcgyja3cj" />
                  <div data-id="element-862" data-mp="m093o3kusmaz">
                    <div className="text-sm font-medium text-brand-navy" data-id="element-863" data-mp="19rwllw050n14">
                      Call Us Directly
                    </div>
                    <a href="tel:+2676540244" className="text-brand-red font-semibold hover:underline" data-id="element-864" data-mp="1epy70nl1cyfr">
                      +267 6540244
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-865" data-mp="3lhqnpzrn05ro">
                  <Clock className="w-5 h-5 text-brand-gold shrink-0 mt-0.5" data-id="element-866" data-mp="3lg5t6a5bomtt" />
                  <div data-id="element-867" data-mp="3qhbngcemsi5g">
                    <div className="text-sm font-medium text-brand-navy" data-id="element-868" data-mp="3qeh1ggfyyp9x">
                      Operating Hours
                    </div>
                    <div className="text-sm text-slate-600" data-id="element-869" data-mp="3dyhatwjx9oxv">
                      Mon - Fri: 8:00 AM - 6:00 PM
                    </div>
                    <div className="text-sm text-slate-600" data-id="element-870" data-mp="3rx793fih9unt">
                      Sat: 9:00 AM - 1:00 PM
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}